import { Component, OnInit, ChangeDetectorRef, AfterViewInit, Inject, Optional, ViewChild, HostListener, ElementRef } from '@angular/core';
import { CalendarEvent, CalendarView, CalendarWeekViewComponent } from 'angular-calendar';
import moment from 'moment';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ApiService } from '../../../api.service';
import { ToastrService } from 'ngx-toastr';
import { NavbarComponent } from '../../../navbar/navbar.component';
import { Router } from '@angular/router';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Subject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface RosterEventMeta {
	emp_code: string;
	userInitial: string;
	color: string;
}

@Component({
	selector: 'app-roster-scheduler',
	templateUrl: './roster-scheduler.component.html',
	styleUrl: './roster-scheduler.component.scss'
})
export class RosterSchedulerComponent implements AfterViewInit {
	allUserList: any[] = [];
	userList: any[] = [];
	useMultiSelect = false;
	disableDepartment = false;
	filters: any = {
		emp_code: null,
		selectedEmpCodes: [],
		department: null
	};

	// Calendar View Configurations
	@ViewChild('calendarContainer') calendarContainer!: ElementRef;
	@ViewChild("calendarWeekViewComponent") calendarWeekViewComponent!: CalendarWeekViewComponent;
	view: CalendarView = CalendarView.Week;
	viewDate: Date = new Date();
	refresh = new Subject<void>();
	events: CalendarEvent<RosterEventMeta>[] = [];

	dayStartHour = 0;
	dayEndHour = 23;
	hourSegmentsWeekView = 2;
	hourDurationWeekView = 60;
	hourSegmentHeightWeekView = 20;
	minimumEventHeightWeekView = 10;

	// Drag-to-select States
	dragSelecting = false;
	dragStartSegment: Date | null = null;
	dragEndSegment: Date | null = null;

	weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
	months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	rosterLegend: any[] = [];
	showPdfLegend = false;
	constructor(
		private apiService: ApiService,
		private toastr: ToastrService,
		private router: Router,
		private cdRef: ChangeDetectorRef,
		@Optional() @Inject(NavbarComponent) public navbar: NavbarComponent
	) { }

	ngAfterViewInit(): void {
		this.apiService.departmentList$.subscribe((departments: any[]) => {
			if (departments && departments.length) {
				if (departments.length === 1) {
					//If departments has only 1 entry then other are removed as user is Lead(with department) or Assignee
					this.disableDepartment = true;
					this.filters.department = departments[0].id;
				}
				this.apiService.userData$.subscribe((user: any) => {
					if (user) {
						if (!this.navbar.hasAttendanceAccess()) {
							this.filters.emp_code = user.emp_code;
						}
						this.apiService.userList$.subscribe((userList) => {
							if (userList) {
								this.allUserList = userList.filter((u) => u.emp_code);
								this.getUserList();
							}
						});
					}
				});
			}
		});
	}

	// Global mouseup release listener to complete drag selection anywhere on the screen
	@HostListener('document:mouseup')
	onDocumentMouseUp(): void {
		if (this.dragSelecting) {
			this.endDragSelect();
		}
	}

	getUserList() {
		if (this.filters.department) {
			this.userList = this.allUserList.filter((u) => u.department === this.filters.department);
		} else {
			this.userList = this.allUserList.filter((u) => true);
		}
	}

	onDepartmentChange() {
		this.getUserList();
		const validEmpCodes = this.userList.map(u => u.emp_code);
		if (this.useMultiSelect) {
			this.filters.selectedEmpCodes = this.filters.selectedEmpCodes.filter(code => validEmpCodes.includes(code));
		} else {
			if (!validEmpCodes.includes(this.filters.emp_code)) {
				this.filters.emp_code = null;
			}
		}
	}

	startDragSelectOnEvent(event: CalendarEvent<RosterEventMeta>, mouseEvent: MouseEvent): void {
		if (this.useMultiSelect) {
			if (!this.filters.selectedEmpCodes || this.filters.selectedEmpCodes.length === 0) {
				this.toastr.info('Please select at least one employee from the dropdown before dragging.', 'Info');
				return;
			}
		} else {
			if (!this.filters.emp_code) {
				this.toastr.info('Please select employee from the dropdown before dragging.', 'Info');
				return;
			}
		}
		const targetElement = mouseEvent.currentTarget as HTMLElement;
		if (!targetElement) return;

		// Determine vertical offset within the event container
		const rect = targetElement.getBoundingClientRect();
		const offsetY = mouseEvent.clientY - rect.top;

		// Calculate height of one segment (e.g. 30px per 30 min)
		const segmentHeight = this.hourSegmentHeightWeekView;
		const clickedSegmentIndex = Math.floor(offsetY / segmentHeight);

		// Each segment represents (60 / hourSegmentsWeekView) minutes
		const minutesPerSegment = this.hourDurationWeekView / this.hourSegmentsWeekView;
		const clickedDate = new Date(event.start.getTime() + clickedSegmentIndex * minutesPerSegment * 60 * 1000);

		// Ensure clicked date doesn't exceed event boundary
		if (event.end && clickedDate >= event.end) {
			this.startDragSelect(new Date(event.end.getTime() - minutesPerSegment * 60 * 1000));
		} else {
			this.startDragSelect(clickedDate);
		}
	}

	// Safety flag to prevent drag completion during deletion
	isDeleting = false;

	// --- Remove Single Event Slot ---
	removeEvent(eventToDelete: CalendarEvent<RosterEventMeta>, mouseEvent?: MouseEvent): void {
		if (mouseEvent) {
			mouseEvent.stopPropagation();
			mouseEvent.stopImmediatePropagation();
			mouseEvent.preventDefault();
		}

		this.isDeleting = true;
		this.resetDrag();

		this.events = this.events.filter(event => event !== eventToDelete);
		this.refresh.next();
		/* this.toastr.success('Time slot removed', 'Success'); */

		// Reset safety flag shortly after execution
		setTimeout(() => {
			this.isDeleting = false;
		}, 100);
	}

	endDragSelect(): void {
		if (!this.dragSelecting || !this.dragStartSegment) {
			this.resetDrag();
			return;
		}

		const startSegment = this.dragStartSegment;
		const endSegment = this.dragEndSegment ? this.dragEndSegment : this.dragStartSegment;

		const start = startSegment < endSegment ? startSegment : endSegment;
		const end = startSegment < endSegment ? endSegment : startSegment;

		// Add segment duration (e.g., 30 min) to cover end slot
		const segmentDurationMs = (this.hourDurationWeekView / this.hourSegmentsWeekView) * 60 * 1000;
		const finalEnd = new Date(end.getTime() + segmentDurationMs);

		this.onSlotDragged_With_Leave_Validation(start, finalEnd);
		this.resetDrag();
	}

	// --- Drag-to-Select Logic ---

	startDragSelect(date: Date): void {
		if (this.useMultiSelect) {
			if (!this.filters.selectedEmpCodes || this.filters.selectedEmpCodes.length === 0) {
				this.toastr.info('Please select at least one employee from the dropdown before dragging.', 'Info');
				return;
			}
		} else {
			if (!this.filters.emp_code) {
				this.toastr.info('Please select employee from the dropdown before dragging.', 'Info');
				return;
			}
		}
		this.dragSelecting = true;
		this.dragStartSegment = date;
		this.dragEndSegment = date;
	}

	updateDragSelect(date: Date): void {
		if (this.dragSelecting) {
			this.dragEndSegment = date;
		}
	}

	resetDrag(): void {
		this.dragSelecting = false;
		this.dragStartSegment = null;
		this.dragEndSegment = null;
	}

	onSlotDragged_Without_Leave_Validation(start: Date, end: Date): void {
		let selectedUsers: any[] = [];
		if (this.useMultiSelect) {
			selectedUsers = this.userList.filter(u => this.filters.selectedEmpCodes.includes(u.emp_code));
		} else {
			selectedUsers = this.userList.filter(u => this.filters.emp_code == u.emp_code);
		}
		let updatedEvents = [...this.events];

		selectedUsers.forEach((user) => {
			const userColor = user.color ? user.color : '#000000';
			const initial = this.getInitials(user.full_name);

			let newStart = new Date(start.getTime());
			let newEnd = new Date(end.getTime());

			// Find existing events for this user that touch or overlap with the new slot
			const overlappingOrAdjacentEvents = updatedEvents.filter(e => {
				if (e.meta?.emp_code !== user.emp_code || !e.end) return false;

				const existingStart = e.start.getTime();
				const existingEnd = e.end.getTime();

				// Check if new slot overlaps or connects directly at start/end bounds
				const isTouchingOrOverlapping =
					(newStart.getTime() <= existingEnd && newEnd.getTime() >= existingStart);

				return isTouchingOrOverlapping;
			});

			// Remove the old segments so we can replace them with a single merged event
			if (overlappingOrAdjacentEvents.length > 0) {
				updatedEvents = updatedEvents.filter(e => !overlappingOrAdjacentEvents.includes(e));

				// Calculate outer boundary start/end dates
				overlappingOrAdjacentEvents.forEach(e => {
					if (e.start.getTime() < newStart.getTime()) {
						newStart = new Date(e.start.getTime());
					}
					if (e.end && e.end.getTime() > newEnd.getTime()) {
						newEnd = new Date(e.end.getTime());
					}
				});
			}

			// Push the newly merged continuous event
			updatedEvents.push({
				start: newStart,
				end: newEnd,
				title: user.full_name,
				color: {
					primary: userColor,
					secondary: this.hexToRgba(userColor, 0.2)
				},
				meta: {
					emp_code: user.emp_code,
					userInitial: initial,
					color: userColor
				}
			});
		});

		this.events = updatedEvents;
		this.refresh.next();
	}

	onSlotDragged_With_Leave_Validation(start: Date, end: Date): void {
		let selectedUsers: any[] = [];
		if (this.useMultiSelect) {
			selectedUsers = this.userList.filter(u => this.filters.selectedEmpCodes.includes(u.emp_code));
		} else {
			selectedUsers = this.userList.filter(u => this.filters.emp_code == u.emp_code);
		}

		if (selectedUsers.length === 0) return;

		// Validate leaves before adding events to calendar
		this.validateLeaveForUsers_InDate(selectedUsers, start, end, () => {
			let updatedEvents = [...this.events];

			selectedUsers.forEach((user) => {
				const userColor = user.color ? user.color : '#000000';
				const initial = this.getInitials(user.full_name);

				let newStart = new Date(start.getTime());
				let newEnd = new Date(end.getTime());

				// Find existing events for this user that touch or overlap with the new slot
				const overlappingOrAdjacentEvents = updatedEvents.filter(e => {
					if (e.meta?.emp_code !== user.emp_code || !e.end) return false;

					const existingStart = e.start.getTime();
					const existingEnd = e.end.getTime();

					return (newStart.getTime() <= existingEnd && newEnd.getTime() >= existingStart);
				});

				// Remove the old segments so we can replace them with a single merged event
				if (overlappingOrAdjacentEvents.length > 0) {
					updatedEvents = updatedEvents.filter(e => !overlappingOrAdjacentEvents.includes(e));

					overlappingOrAdjacentEvents.forEach(e => {
						if (e.start.getTime() < newStart.getTime()) {
							newStart = new Date(e.start.getTime());
						}
						if (e.end && e.end.getTime() > newEnd.getTime()) {
							newEnd = new Date(e.end.getTime());
						}
					});
				}

				// Push the newly merged continuous event
				updatedEvents.push({
					start: newStart,
					end: newEnd,
					title: user.full_name,
					color: {
						primary: userColor,
						secondary: this.hexToRgba(userColor, 0.2)
					},
					meta: {
						emp_code: user.emp_code,
						userInitial: initial,
						color: userColor
					}
				});
			});

			this.events = updatedEvents;
			this.refresh.next();
		});
	}

	private validateLeaveForUsers_InDate_OutDate(users: any[], startDate: Date, endDate: Date, callback: () => void): void {
		// 1. Collect all unique dates across the dragged range
		const datesToCheck: string[] = [];
		let curr = moment(startDate).startOf('day');
		const end = moment(endDate).startOf('day');

		while (curr.isSameOrBefore(end)) {
			datesToCheck.push(curr.format('YYYY-MM-DD'));
			curr.add(1, 'day');
		}

		// 2. Build array of checkLeave requests for all selected users and dates
		const checkRequests: Observable<any>[] = [];

		users.forEach(user => {
			datesToCheck.forEach(dateStr => {
				checkRequests.push(
					this.apiService.checkLeave({ emp_code: user.emp_code, date: dateStr }).pipe(
						map(() => ({ hasLeave: false, user, date: dateStr })),
						catchError(() => of({ hasLeave: true, user, date: dateStr }))
					)
				);
			});
		});

		if (checkRequests.length === 0) {
			callback();
			return;
		}

		// 3. Pass checkRequests directly as the single array argument
		forkJoin(checkRequests).subscribe((results: any[]) => {
			const leaveConflicts = results.filter(r => r.hasLeave);

			if (leaveConflicts.length > 0) {
				const conflictDetails = leaveConflicts
					.map(c => `${c.user.full_name || c.user.emp_code} on ${c.date}`)
					.join(', ');

				this.toastr.error(`Leave Conflict: ${conflictDetails}`, 'Leave Error', {
					timeOut: 4000,
					tapToDismiss: true,
					closeButton: true
				});
				return; // Block event addition due to leave conflict
			}

			// Proceed with adding events if no leave conflicts exist
			callback();
		});
	}

	private validateLeaveForUsers_InDate(users: any[], startDate: Date, endDate: Date, callback: () => void): void {
		// Extract unique start dates (in_date only) formatted as YYYY-MM-DD
		const datesToCheck: string[] = [];
		const inDateStr = moment(startDate).format('YYYY-MM-DD');
		datesToCheck.push(inDateStr);

		// Build checkLeave requests for all selected users for the in_date
		const checkRequests: Observable<any>[] = [];

		users.forEach(user => {
			datesToCheck.forEach(dateStr => {
				checkRequests.push(
					this.apiService.checkLeave({ emp_code: user.emp_code, date: dateStr }).pipe(
						map(() => ({ hasLeave: false, user, date: dateStr })),
						catchError(() => of({ hasLeave: true, user, date: dateStr }))
					)
				);
			});
		});

		if (checkRequests.length === 0) {
			callback();
			return;
		}

		// Pass checkRequests directly as an array
		forkJoin(checkRequests).subscribe((results: any[]) => {
			const leaveConflicts = results.filter(r => r.hasLeave);

			if (leaveConflicts.length > 0) {
				const conflictDetails = leaveConflicts
					.map(c => `${c.user.full_name || c.user.emp_code} on ${c.date}`)
					.join(', ');

				this.toastr.error(`Leave Conflict: ${conflictDetails}`, 'Leave Error', {
					timeOut: 4000,
					tapToDismiss: true,
					closeButton: true
				});
				return; // Block event addition due to leave conflict on in_date
			}

			// Proceed with adding events if no leave conflicts exist
			callback();
		});
	}

	getInitials(fullName: string): string {
		if (!fullName) return '';
		const parts = fullName.trim().split(' ');
		if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}

	hexToRgba(hex: string, alpha: number): string {
		let c: any;
		if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
			c = hex.substring(1).split('');
			if (c.length === 3) {
				c = [c[0], c[0], c[1], c[1], c[2], c[2]];
			}
			c = '0x' + c.join('');
			return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${alpha})`;
		}
		return 'rgba(0, 0, 0, 0.15)';
	}

	getEventHourCount(event: CalendarEvent): number {
		try {
			const start = new Date(event.start).getTime();
			const end = new Date(event.end || event.start).getTime();
			const diffMinutes = Math.round((end - start) / (1000 * 60));
			const minutesPerSegment = this.hourDurationWeekView / this.hourSegmentsWeekView;
			const segments = Math.max(1, Math.round(diffMinutes / minutesPerSegment));
			return segments;
		} catch (e) {
			return 1;
		}
	}

	getRepeatedBadges(event: CalendarEvent): { text: string; visible: boolean }[] {
		/* const start = new Date(event.start);
		const end = new Date(event.end || event.start); */
		const count = this.getEventHourCount(event);
		const initial = event.meta?.userInitial || '';

		const items: any[] = [];
		for (let i = 0; i < count; i++) {
			let visible = true;
			/* // Hide first cell text if event starts at hh:30 and spans multiple segments
			if (i === 0 && start.getMinutes() > 0 && count > 1) {
				visible = false;
			}
			// Hide last cell text if event ends at hh:30 and spans multiple segments
			if (i === count - 1 && end.getMinutes() > 0 && count > 1) {
				visible = false;
			} */
			items.push({ text: initial, visible });
		}
		return items;
	}

	exportLoader = false;
	exportToPDFOld(container: HTMLElement): void {
		if (!container) {
			this.toastr.error('Invalid element reference.', 'Error');
			return;
		}

		this.exportLoader = true;
		this.cdRef.detectChanges();

		setTimeout(async () => {
			try {
				// 1. Temporarily expand inner scroll containers
				const scrollableElements = container.querySelectorAll('.cal-time-events, .calendar-scroll-container');
				const originalStyles: { el: HTMLElement; overflow: string; height: string }[] = [];

				scrollableElements.forEach((el: Element) => {
					const htmlEl = el as HTMLElement;
					originalStyles.push({
						el: htmlEl,
						overflow: htmlEl.style.overflow,
						height: htmlEl.style.height
					});
					htmlEl.style.overflow = 'visible';
					htmlEl.style.height = 'auto';
				});

				// 2. Capture full expanded canvas
				const canvas = await html2canvas(container, {
					scale: 2,
					useCORS: true,
					scrollY: -window.scrollY,
					windowHeight: container.scrollHeight,
					height: container.scrollHeight,
					width: container.scrollWidth
				});

				// 3. Restore original container styles
				originalStyles.forEach(item => {
					item.el.style.overflow = item.overflow;
					item.el.style.height = item.height;
				});

				// 4. Generate Single-Page Scaled PDF
				const imgData = canvas.toDataURL('image/png');

				// Create landscape PDF with standard A4 width (297mm) and dynamic height to prevent canvas clipping
				const pdfWidth = 297;
				const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

				const pdf = new jsPDF('landscape', 'mm', [pdfWidth, pdfHeight]);
				pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

				pdf.save(`Roster_${moment(this.viewDate).format('YYYY-MM-DD')}.pdf`);

			} catch (err) {
				this.toastr.error('Failed to generate PDF.', 'Error');
			} finally {
				this.exportLoader = false;
				this.cdRef.detectChanges();
			}
		}, 100);
	}

	exportToPDF(container: HTMLElement): void {
		if (!container) {
			this.toastr.error('Invalid element reference.', 'Error');
			return;
		}

		// Populate legend ONLY with users who have events added on the calendar
		const uniqueEmpMap = new Map();
		this.events.forEach(event => {
			const emp_code = event.meta?.emp_code;
			if (emp_code && !uniqueEmpMap.has(emp_code)) {
				const fullName = event.title || '';
				const color = event.color?.primary || event.meta?.color || '#000000';
				uniqueEmpMap.set(emp_code, {
					name: fullName,
					color: color,
					initials: event.meta?.userInitial || this.getInitials(fullName)
				});
			}
		});
		const rawLegend = Array.from(uniqueEmpMap.values()) as any[];
		this.rosterLegend = rawLegend.filter((item, index, self) =>
			index === self.findIndex((t) => t.name === item.name)
		);

		this.showPdfLegend = true; // Show legend for PDF capture
		this.exportLoader = true;
		this.cdRef.detectChanges();

		const parentCol = container.parentElement as HTMLElement;
		const originalWidth = parentCol ? parentCol.style.width : '';
		const originalFlex = parentCol ? parentCol.style.flex : '';
		const originalMaxWidth = parentCol ? parentCol.style.maxWidth : '';

		// Save original container styles for padding & background
		const originalPadding = container.style.padding;
		const originalBackground = container.style.backgroundColor;

		if (parentCol) {
			parentCol.style.width = '100%';
			parentCol.style.flex = '0 0 100%';
			parentCol.style.maxWidth = '100%';
		}

		// Apply PDF padding and white background
		container.style.padding = '20px';
		container.style.backgroundColor = '#ffffff';

		// Temporarily hide current time markers inside the container
		const currentTimeMarkers = container.querySelectorAll('.cal-current-time-marker') as NodeListOf<HTMLElement>;
		const originalMarkerDisplays: string[] = [];
		currentTimeMarkers.forEach(marker => {
			originalMarkerDisplays.push(marker.style.display);
			marker.style.display = 'none';
		});

		setTimeout(async () => {
			try {
				const canvas = await html2canvas(container, {
					scale: 2,
					useCORS: true,
					windowWidth: container.scrollWidth,
					windowHeight: container.scrollHeight + 60
				});

				const imgData = canvas.toDataURL('image/png');
				const pdfWidth = 297; // Landscape A4 width in mm
				const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

				const pdf = new jsPDF('landscape', 'mm', [pdfWidth, pdfHeight]);
				pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

				const titleElement = container.querySelector('#weekViewTitleDoNotRemove');
				const dateRangeText = titleElement ? titleElement.textContent?.trim() : moment(this.viewDate).format('YYYY-MM-DD');
				pdf.save(`Roster_${dateRangeText}.pdf`);

			} catch (err) {
				this.toastr.error('Failed to generate PDF.', 'Error');
			} finally {
				this.showPdfLegend = false; // Hide legend after PDF capture

				// Restore current time markers
				currentTimeMarkers.forEach((marker, index) => {
					marker.style.display = originalMarkerDisplays[index];
				});

				// Restore original container padding and background
				container.style.padding = originalPadding;
				container.style.backgroundColor = originalBackground;

				if (parentCol) {
					parentCol.style.width = originalWidth;
					parentCol.style.flex = originalFlex;
					parentCol.style.maxWidth = originalMaxWidth;
				}

				this.exportLoader = false;
				this.cdRef.detectChanges();
			}
		}, 150);
	}

	submitLoader = false;
	/* submitRoster(): void {
		if (!this.events || this.events.length === 0) {
			this.toastr.info('No roster slots to submit.', 'Info');
			return;
		}
		this.submitLoader = true;

		// 1. Group calendar events by employee code
		const groupedByEmp = this.events.reduce((acc, event) => {
			const emp_code = event.meta?.emp_code;
			if (!emp_code) return acc;

			if (!acc[emp_code]) {
				acc[emp_code] = [];
			}
			acc[emp_code].push(event);
			return acc;
		}, {} as Record<string, CalendarEvent<RosterEventMeta>[]>);

		// 2. Map grouped events into the target JSON payload structure
		const payload = Object.keys(groupedByEmp).map((emp_code) => {
			const empEvents = groupedByEmp[emp_code];

			// Collect all start & end timestamps to compute global from_date / to_date
			const allDates = empEvents.flatMap(e => [
				e.start.getTime(),
				e.end ? e.end.getTime() : e.start.getTime()
			]);

			const minDate = new Date(Math.min(...allDates));
			const maxDate = new Date(Math.max(...allDates));

			// Build line items for this specific employee
			const line_items = empEvents.map(e => ({
				in_date: moment(e.start).format('YYYY-MM-DD'),
				in_time: moment(e.start).format('HH:mm:ss'),
				out_date: moment(e.end).format('YYYY-MM-DD'),
				out_time: moment(e.end).format('HH:mm:ss')
			}));

			return {
				emp_code: emp_code,
				from_date: moment(minDate).format('YYYY-MM-DD'),
				to_date: moment(maxDate).format('YYYY-MM-DD'),
				line_items: line_items,
				deleted_items: []
			};
		});
		this.apiService.createUpdateRoster(payload).subscribe({
			next: (data: any) => {
				this.submitLoader = false;
				this.toastr.success("Saved successfully", 'Success', {
					timeOut: 3000,
					tapToDismiss: true,
					closeButton: true
				});
				this.router.navigate(['/app/attendance']);
			}, error: (error) => {
				this.submitLoader = false;
				if (error.status == 401) { return }
				let errorMsg = error.error;
				this.toastr.error(errorMsg, 'Error', {
					timeOut: 3000,
					tapToDismiss: true,
					closeButton: true
				});
			}
		});
	} */

	getFromToDate() {
		return {
			from_date: moment(this.calendarWeekViewComponent.view.period.start).format('YYYY-MM-DD'),
			to_date: moment(this.calendarWeekViewComponent.view.period.end).format('YYYY-MM-DD')
			/* from_date: moment(this.viewDate).isoWeekday(1).format('YYYY-MM-DD'),
			to_date: moment(this.viewDate).isoWeekday(7).format('YYYY-MM-DD') */
		};
	}

	submitRoster(): void {
		if (!this.events || this.events.length === 0) {
			this.toastr.info('No roster slots to submit.', 'Info');
			return;
		}
		this.submitLoader = true;
		// 1. Group calendar events by employee code
		const groupedByEmp = this.events.reduce((acc, event) => {
			const emp_code = event.meta?.emp_code;
			if (!emp_code) return acc;

			if (!acc[emp_code]) {
				acc[emp_code] = [];
			}
			acc[emp_code].push(event);
			return acc;
		}, {} as Record<string, CalendarEvent<RosterEventMeta>[]>);

		// 2. Map grouped events into target JSON payload
		const payload = Object.keys(groupedByEmp).map((emp_code) => {
			const empEvents = groupedByEmp[emp_code];

			// Build line items for this specific employee
			const line_items = empEvents.map(e => ({
				in_date: moment(e.start).format('YYYY-MM-DD'),
				in_time: moment(e.start).format('HH:mm:ss'),
				out_date: moment(e.end).format('YYYY-MM-DD'),
				out_time: moment(e.end).format('HH:mm:ss')
			}));

			return {
				emp_code: emp_code,
				line_items: line_items,
				deleted_items: [],
				...this.getFromToDate()
			};
		});
		this.apiService.createUpdateRoster(payload).subscribe({
			next: (data: any) => {
				this.submitLoader = false;
				this.toastr.success("Saved successfully", 'Success', {
					timeOut: 3000,
					tapToDismiss: true,
					closeButton: true
				});
				this.router.navigate(['/app/attendance']);
			}, error: (error) => {
				this.submitLoader = false;
				if (error.status == 401) { return }
				let errorMsg = error.error;
				this.toastr.error(errorMsg, 'Error', {
					timeOut: 3000,
					tapToDismiss: true,
					closeButton: true
				});
			}
		});
	}
}